import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getUiLocale } from '@/lib/dates';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { SuiteIcon } from '@/components/shared/SuiteIcon';
import {
  Heart, Download, Loader2, CheckCircle2, FileText, Building2,
  Users, Trophy, ClipboardList, ArrowLeft, Sparkles, Send,
} from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';
import { api, formatApiError } from '@/lib/api';
import SuiteOpsPanel from '@/components/admin/SuiteOpsPanel';

/**
 * Sprint TH-C — TalentHeart Admin workspace.
 *
 * Replaces the previous /admin/talentheart SuiteConsole shell (always empty,
 * because TalentHeart questionnaires are not enumerated as Library rows on
 * that surface) with a real per-suite workspace mirroring the philosophy of
 * /admin/talentmap and /admin/climara:
 *
 *   • Companies list (advisor-scoped automatically by the backend).
 *   • Drill-in: KPIs, recent surveys, generated reports.
 *   • One-click "Generate report" (registers a versioned snapshot in
 *     `talentheart_reports` and lets HM Admin/Advisor re-download the PDF
 *     at any time).
 *   • Release-to-client action for governance parity with Climara.
 */

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(getUiLocale()); } catch { return iso.slice(0, 10); }
};
const fmtPct = (n) => (n == null ? '—' : `${Math.round(Number(n))}%`);

function CompaniesView({ onPick }) {
  const { t, i18n } = useTranslation();
  const isES = i18n.language === 'es';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('contracted');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.talentHeartListCompanies();
      setItems(data?.items || []);
    } catch (e) {
      toast.error(isES ? 'No se pudo cargar' : 'Could not load', { description: formatApiError(e) });
    } finally { setLoading(false); }
  }, [isES]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const norm = q.trim().toLowerCase();
    return (items || []).filter((c) => {
      if (filter === 'contracted' && !c.contracted) return false;
      if (filter === 'with-activity' && !(c.last_survey || c.last_report || c.recognitions_total)) return false;
      if (!norm) return true;
      const blob = `${c.company_name || ''} ${c.industry || ''} ${c.country || ''}`.toLowerCase();
      return blob.includes(norm);
    });
  }, [items, q, filter]);

  return (
    <div className="space-y-4" data-testid="admin-theart-companies-view">
      <Card className="p-4 flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex items-center gap-2 flex-1">
          <Heart className="h-4 w-4 text-pink-500" />
          <h2 className="text-sm font-semibold tracking-wide">
            {isES ? 'TalentHeart · Espacio HM' : 'TalentHeart · HM Workspace'}
          </h2>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[200px]" data-testid="admin-theart-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="contracted">{isES ? 'Contratadas (suite activa)' : 'Contracted (suite on)'}</SelectItem>
            <SelectItem value="with-activity">{isES ? 'Con actividad reciente' : 'With recent activity'}</SelectItem>
            <SelectItem value="all">{isES ? 'Todas' : 'All companies'}</SelectItem>
          </SelectContent>
        </Select>
        <Input
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder={isES ? 'Buscar empresa…' : 'Search company…'}
          className="md:w-[260px]"
          data-testid="admin-theart-search"
        />
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3" data-testid="admin-theart-companies-list">
        {loading ? (
          <div className="col-span-full text-xs text-muted-foreground flex items-center gap-2 px-2">
            <Loader2 className="h-3 w-3 animate-spin" />
            {isES ? 'Cargando…' : 'Loading…'}
          </div>
        ) : filtered.length === 0 ? (
          <Card className="col-span-full p-6 text-center text-xs text-muted-foreground" data-testid="admin-theart-empty">
            {isES ? 'No hay empresas para mostrar con ese filtro.' : 'No companies matching this filter.'}
          </Card>
        ) : filtered.map((c) => (
          <Card
            key={c.company_id}
            className="p-4 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => onPick(c)}
            data-testid={`admin-theart-company-${c.company_id}`}
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex items-center gap-2 min-w-0">
                <Building2 className="h-4 w-4 text-pink-500 shrink-0" />
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate" title={c.company_name}>{c.company_name}</div>
                  <div className="text-xs text-muted-foreground truncate">{c.industry || '—'} · {c.country || '—'}</div>
                </div>
              </div>
              {c.contracted ? (
                <Badge variant="success" className="text-xs">{isES ? 'Activa' : 'Active'}</Badge>
              ) : (
                <Badge variant="outline" className="text-xs">{isES ? 'Sin suite' : 'No suite'}</Badge>
              )}
            </div>
            <div className="grid grid-cols-3 gap-2 mt-3">
              <Metric icon={Users} label={isES ? 'Plantilla' : 'Headcount'} value={c.headcount} />
              <Metric icon={Trophy} label={isES ? 'Reconoc.' : 'Recogn.'} value={c.recognitions_total} />
              <Metric icon={ClipboardList} label={isES ? 'Encuesta' : 'Survey'}
                      value={c.last_survey ? (c.last_survey.status === 'active'
                        ? (isES ? 'Activa' : 'Active')
                        : (isES ? 'Cerrada' : 'Closed')) : '—'} small />
            </div>
            <div className="mt-3 text-xs flex items-center justify-between text-muted-foreground">
              <span>
                {c.last_report
                  ? `${isES ? 'Reporte' : 'Report'}: ${c.last_report.status === 'released' ? (isES ? 'liberado' : 'released') : 'draft'} · ${fmtDate(c.last_report.generated_at)}`
                  : (isES ? 'Sin reportes generados' : 'No reports generated yet')}
              </span>
              <span className="text-pink-600">{isES ? 'Abrir →' : 'Open →'}</span>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value, small }) {
  return (
    <div className="flex flex-col items-start">
      <div className="flex items-center gap-1 text-xs text-muted-foreground">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className={small ? 'text-xs font-medium mt-0.5' : 'text-sm font-bold mt-0.5'}>{value ?? '—'}</div>
    </div>
  );
}

function DispatchesTab({ companyId, isES }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/admin/theart/companies/${companyId}/dispatches`);
        if (alive) setData(res.data);
      } catch (e) {
        toast.error(isES ? 'No se pudieron cargar los envíos' : 'Could not load dispatches', { description: formatApiError(e) });
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [companyId, isES]);

  const audienceLabel = (a) => {
    if (a === 'department') return isES ? 'Departamento' : 'Department';
    if (a === 'employees') return isES ? 'Empleados seleccionados' : 'Selected employees';
    return isES ? 'Toda la empresa' : 'Whole company';
  };

  if (loading) {
    return (
      <Card className="p-4 text-xs flex items-center text-muted-foreground" data-testid="admin-theart-dispatches-loading">
        <Loader2 className="h-3 w-3 animate-spin mr-2" />
        {isES ? 'Cargando envíos…' : 'Loading dispatches…'}
      </Card>
    );
  }
  const surveys = data?.surveys || [];
  const campaigns = data?.profile_campaigns || [];
  return (
    <div className="space-y-3" data-testid="admin-theart-dispatches">
      <Card className="p-3 text-xs" data-testid="admin-theart-surveys-tab">
        <div className="font-medium mb-2">{isES ? 'Encuestas de bienestar (autoservicio del cliente)' : 'Wellbeing surveys (client self-serve)'}</div>
        {surveys.length === 0 ? (
          <div className="text-muted-foreground text-center py-4">
            {isES ? 'Esta empresa aún no ha lanzado encuestas de wellbeing.' : 'No wellbeing surveys launched yet.'}
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-xs text-muted-foreground uppercase border-b">
              <tr>
                <th className="text-left py-1.5">{isES ? 'Encuesta' : 'Survey'}</th>
                <th className="text-left py-1.5">{isES ? 'Destinatarios' : 'Audience'}</th>
                <th className="text-center py-1.5">{isES ? 'Invitados' : 'Invited'}</th>
                <th className="text-center py-1.5">{isES ? 'Respuestas' : 'Responses'}</th>
                <th className="text-left py-1.5">{isES ? 'Límite' : 'Deadline'}</th>
                <th className="text-left py-1.5">{isES ? 'Estado' : 'Status'}</th>
              </tr>
            </thead>
            <tbody>
              {surveys.map((s) => (
                <tr key={s.id} className="border-b last:border-0" data-testid={`admin-theart-dispatch-survey-${s.id}`}>
                  <td className="py-1.5 font-medium">{isES ? s.title_es : s.title_en}</td>
                  <td className="py-1.5">{audienceLabel(s.target_audience)}</td>
                  <td className="py-1.5 text-center tabular-nums">{s.invited || '—'}</td>
                  <td className="py-1.5 text-center tabular-nums">{s.response_count || 0}</td>
                  <td className="py-1.5">{s.deadline ? fmtDate(s.deadline) : '—'}</td>
                  <td className="py-1.5">
                    <Badge variant={s.status === 'active' ? 'success' : 'outline'} className="text-xs">{s.status}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      <Card className="p-3 text-xs" data-testid="admin-theart-profile-campaigns">
        <div className="font-medium mb-2">{isES ? 'Encuestas de perfil personal' : 'Personal profile surveys'}</div>
        {campaigns.length === 0 ? (
          <div className="text-muted-foreground text-center py-4">
            {isES ? 'Sin envíos de perfil personal todavía.' : 'No personal-profile dispatches yet.'}
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-xs text-muted-foreground uppercase border-b">
              <tr>
                <th className="text-left py-1.5">{isES ? 'Enviado' : 'Sent'}</th>
                <th className="text-left py-1.5">{isES ? 'Destinatarios' : 'Audience'}</th>
                <th className="text-center py-1.5">{isES ? 'Invitados' : 'Invited'}</th>
                <th className="text-center py-1.5">{isES ? 'Completados' : 'Completed'}</th>
                <th className="text-left py-1.5">{isES ? 'Límite' : 'Deadline'}</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => (
                <tr key={c.id} className="border-b last:border-0" data-testid={`admin-theart-dispatch-campaign-${c.id}`}>
                  <td className="py-1.5">{fmtDate(c.created_at)}</td>
                  <td className="py-1.5">{audienceLabel(c.target_audience)}</td>
                  <td className="py-1.5 text-center tabular-nums">{c.invited_count}</td>
                  <td className="py-1.5 text-center tabular-nums">{c.completed_count}</td>
                  <td className="py-1.5">{c.deadline ? fmtDate(c.deadline) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function CompanyDetail({ company, onBack }) {
  const { t, i18n } = useTranslation();
  const isES = i18n.language === 'es';
  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [locale, setLocale] = useState(isES ? 'es' : 'en');
  const [releaseDialogId, setReleaseDialogId] = useState(null);
  // TalentHeart 2026-06 — AI narrative draft (reuses the TalentCheck AI engine).
  const [aiBusyId, setAiBusyId] = useState(null);
  const [aiView, setAiView] = useState(null); // { reportId, narrative }

  const loadReports = useCallback(async () => {
    setLoadingReports(true);
    try {
      const data = await adminApi.talentHeartListReports({ company_id: company.company_id });
      setReports(data?.items || []);
    } catch (e) {
      toast.error(isES ? 'No se pudieron cargar los reportes' : 'Could not load reports', { description: formatApiError(e) });
    } finally { setLoadingReports(false); }
  }, [company.company_id, isES]);

  useEffect(() => { loadReports(); }, [loadReports]);

  const generate = async () => {
    setGenerating(true);
    try {
      const d = await adminApi.talentHeartGenerateReport(company.company_id, locale);
      toast.success(isES ? 'Reporte generado' : 'Report generated', { description: `ID ${d.id.slice(0, 8)}` });
      await loadReports();
    } catch (e) {
      toast.error(isES ? 'Error' : 'Error', { description: formatApiError(e) });
    } finally { setGenerating(false); }
  };

  const release = async (rid) => {
    try {
      await adminApi.talentHeartReleaseReport(rid);
      toast.success(isES ? 'Reporte liberado al cliente' : 'Released to client');
      setReleaseDialogId(null);
      await loadReports();
    } catch (e) {
      toast.error(isES ? 'Error' : 'Error', { description: formatApiError(e) });
    }
  };

  const generateAi = async (r) => {
    setAiBusyId(r.id);
    try {
      const res = await adminApi.talentHeartAiNarrative(r.id, locale);
      setAiView({ reportId: r.id, narrative: res.ai_narrative });
      await loadReports();
    } catch (e) {
      const insufficient = e?.response?.status === 422
        && e?.response?.data?.detail === 'insufficient_sample';
      toast.error(
        insufficient
          ? (isES ? 'Muestra insuficiente (n < 5) — el borrador IA requiere al menos 5 respondentes agregados' : 'Insufficient sample (n < 5) — the AI draft requires at least 5 aggregated respondents')
          : formatApiError(e),
      );
    } finally { setAiBusyId(null); }
  };

  const viewAi = async (r) => {
    try {
      const d = await adminApi.talentHeartGetReport(r.id);
      if (d?.ai_narrative) setAiView({ reportId: r.id, narrative: d.ai_narrative });
      else toast.error(isES ? 'Este reporte aún no tiene borrador IA' : 'This report has no AI draft yet');
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const downloadFormat = async (r, ext) => {
    try {
      const res = await api.get(
        `/admin/theart/reports/${r.id}/export.${ext}`,
        { responseType: 'blob' },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `talentheart-${r.id.slice(0, 8)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(isES ? 'Error al descargar' : 'Download failed', { description: formatApiError(e) });
    }
  };
  const downloadPdf = (r) => downloadFormat(r, 'pdf');
  const downloadDocx = (r) => downloadFormat(r, 'docx');
  const downloadXlsx = (r) => downloadFormat(r, 'xlsx');

  return (
    <div className="space-y-4" data-testid="admin-theart-company-detail">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="admin-theart-back">
          <ArrowLeft className="h-3 w-3 mr-1" />
          {isES ? 'Volver' : 'Back'}
        </Button>
        <div className="flex items-center gap-2">
          <Select value={locale} onValueChange={setLocale}>
            <SelectTrigger className="w-[110px] h-8" data-testid="admin-theart-locale">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="es">Español</SelectItem>
              <SelectItem value="en">English</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" onClick={generate} disabled={generating} data-testid="admin-theart-generate-btn">
            {generating ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Sparkles className="h-3 w-3 mr-1" />}
            {isES ? 'Generar reporte' : 'Generate report'}
          </Button>
        </div>
      </div>

      <Card className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <Building2 className="h-4 w-4 text-pink-500" />
          <h2 className="text-base font-semibold">{company.company_name}</h2>
          {company.contracted && <Badge variant="success" className="text-xs">{isES ? 'Suite activa' : 'Suite active'}</Badge>}
        </div>
        <div className="text-xs text-muted-foreground">{company.industry || '—'} · {company.country || '—'}</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          <KpiTile icon={Users} label={isES ? 'Plantilla' : 'Headcount'} value={company.headcount} />
          <KpiTile icon={Trophy} label={isES ? 'Reconocimientos' : 'Recognitions'} value={company.recognitions_total} />
          <KpiTile icon={ClipboardList}
                   label={isES ? 'Última encuesta' : 'Last survey'}
                   value={company.last_survey ? (company.last_survey.title || (isES ? 'Sin título' : 'Untitled')) : '—'} small />
          <KpiTile icon={FileText}
                   label={isES ? 'Último reporte' : 'Last report'}
                   value={company.last_report
                     ? `${company.last_report.status === 'released' ? (isES ? 'Liberado' : 'Released') : 'Draft'} · ${fmtDate(company.last_report.generated_at)}`
                     : '—'} small />
        </div>
      </Card>

      <Tabs defaultValue="reports">
        <TabsList>
          <TabsTrigger value="reports" data-testid="admin-theart-tab-reports">{isES ? 'Reportes' : 'Reports'}</TabsTrigger>
          <TabsTrigger value="surveys" data-testid="admin-theart-tab-surveys">{isES ? 'Encuestas y envíos' : 'Surveys & dispatches'}</TabsTrigger>
        </TabsList>
        <TabsContent value="reports" className="mt-3">
          <Card className="p-3">
            {loadingReports ? (
              <div className="text-xs text-muted-foreground flex items-center gap-2 px-2 py-4">
                <Loader2 className="h-3 w-3 animate-spin" />
                {isES ? 'Cargando…' : 'Loading…'}
              </div>
            ) : reports.length === 0 ? (
              <div className="text-xs text-muted-foreground px-2 py-6 text-center" data-testid="admin-theart-no-reports">
                {isES ? 'Sin reportes registrados. Pulsa "Generar reporte" arriba.' : 'No reports yet. Click "Generate report" above.'}
              </div>
            ) : (
              <table className="w-full text-xs" data-testid="admin-theart-reports-table">
                <thead className="text-xs text-muted-foreground uppercase border-b">
                  <tr>
                    <th className="text-left py-2">ID</th>
                    <th className="text-left py-2">{isES ? 'Generado' : 'Generated'}</th>
                    <th className="text-left py-2">{isES ? 'Estado' : 'Status'}</th>
                    <th className="text-left py-2">{isES ? 'Idioma' : 'Locale'}</th>
                    <th className="text-left py-2">KPIs</th>
                    <th className="text-right py-2">{isES ? 'Acciones' : 'Actions'}</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((r) => (
                    <tr key={r.id} className="border-b last:border-0" data-testid={`admin-theart-report-row-${r.id}`}>
                      <td className="py-2 font-mono">{r.id.slice(0, 8)}</td>
                      <td className="py-2">{fmtDate(r.generated_at)}</td>
                      <td className="py-2">
                        {r.status === 'released' ? (
                          <Badge variant="success" className="text-xs">
                            <CheckCircle2 className="h-2.5 w-2.5 mr-1" />
                            {isES ? 'Liberado' : 'Released'}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">Draft</Badge>
                        )}
                      </td>
                      <td className="py-2 uppercase">{r.locale}</td>
                      <td className="py-2 text-xs text-muted-foreground">
                        H {fmtPct(r.kpis?.happiness_pct)} · E {fmtPct(r.kpis?.engagement_pct)} · P {fmtPct(r.kpis?.participation_pct)}
                      </td>
                      <td className="py-2 text-right space-x-1">
                        <Button size="sm" variant="ghost"
                                onClick={() => (r.has_ai_narrative ? viewAi(r) : generateAi(r))}
                                disabled={aiBusyId === r.id}
                                title={isES ? 'Borrador narrativo IA (requiere revisión profesional)' : 'AI narrative draft (requires professional review)'}
                                data-testid={`admin-theart-ai-${r.id}`}>
                          {aiBusyId === r.id
                            ? <Loader2 className="h-3 w-3 animate-spin" />
                            : <Sparkles className="h-3 w-3" />} IA
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => downloadPdf(r)}
                                title="PDF" data-testid={`admin-theart-pdf-${r.id}`}>
                          <Download className="h-3 w-3" /> PDF
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => downloadDocx(r)}
                                title="DOCX" data-testid={`admin-theart-docx-${r.id}`}>
                          <Download className="h-3 w-3" /> DOCX
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => downloadXlsx(r)}
                                title="XLSX" data-testid={`admin-theart-xlsx-${r.id}`}>
                          <Download className="h-3 w-3" /> XLSX
                        </Button>
                        {r.status !== 'released' && (
                          <Button size="sm" variant="ghost"
                                  onClick={() => setReleaseDialogId(r.id)}
                                  data-testid={`admin-theart-release-${r.id}`}>
                            <Send className="h-3 w-3" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </TabsContent>
        <TabsContent value="surveys" className="mt-3">
          <DispatchesTab companyId={company.company_id} isES={isES} />
        </TabsContent>
      </Tabs>

      {/* AI narrative draft viewer — always labelled as draft requiring review */}
      <Dialog open={!!aiView} onOpenChange={(o) => !o && setAiView(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="admin-theart-ai-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-pink-500" />
              {isES ? 'Borrador narrativo IA — TalentHeart' : 'AI narrative draft — TalentHeart'}
            </DialogTitle>
          </DialogHeader>
          {aiView?.narrative && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="text-xs bg-warning/10 text-warning border-warning/30"
                       data-testid="admin-theart-ai-draft-badge">
                  {isES ? 'BORRADOR — requiere revisión profesional' : 'DRAFT — requires professional review'}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  {aiView.narrative.ai_generated
                    ? (isES ? 'Generado por IA' : 'AI-generated')
                    : (isES ? 'Generado por reglas (sin IA)' : 'Rules-generated (no AI)')}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {fmtDate(aiView.narrative.generated_at)} · {(aiView.narrative.locale || '').toUpperCase()}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                {isES
                  ? 'Basado exclusivamente en datos agregados de TalentHeart (umbral mínimo n ≥ 5). No identifica personas.'
                  : 'Based exclusively on aggregated TalentHeart data (minimum threshold n ≥ 5). No individuals are identified.'}
              </p>
              <pre className="whitespace-pre-wrap text-xs leading-relaxed font-sans border rounded-md p-3 bg-muted/30"
                   data-testid="admin-theart-ai-text">
                {aiView.narrative.text}
              </pre>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!releaseDialogId} onOpenChange={(o) => !o && setReleaseDialogId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isES ? 'Liberar reporte al cliente' : 'Release report to client'}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            {isES ? 'El reporte quedará marcado como liberado y será visible para el cliente.' :
              'The report will be marked as released and visible to the client.'}
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReleaseDialogId(null)}>{isES ? 'Cancelar' : 'Cancel'}</Button>
            <Button onClick={() => release(releaseDialogId)} data-testid="admin-theart-release-confirm">
              {isES ? 'Liberar' : 'Release'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiTile({ icon: Icon, label, value, small }) {
  return (
    <Card className="p-3">
      <div className="flex items-center gap-1 text-xs text-muted-foreground">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className={small ? 'text-xs font-medium mt-1 truncate' : 'text-lg font-bold mt-1'}>{value ?? '—'}</div>
    </Card>
  );
}

export default function AdminTalentHeart() {
  const { t, i18n } = useTranslation();
  const [picked, setPicked] = useState(null);
  return (
    <div className="min-h-screen bg-background">
      <Topbar />
      {/* Founder UAT post-168 (A) — use the full application content width
          like every other suite console (no narrow centered column). */}
      <div className="px-6 lg:px-8 py-6" data-testid="admin-talentheart-page">
        <div className="flex items-center gap-3 mb-4">
          <SuiteIcon code="talentheart" className="h-5 w-5" />
          <div>
            <h1 className="text-xl font-semibold">{t('admin.talentheart.title', { defaultValue: 'TalentHeart' })}</h1>
            <p className="text-xs text-muted-foreground">
              {i18n.language === 'es'
                ? 'Operación de la suite TalentHeart por empresa: encuestas, reconocimientos, reportes y programas.'
                : 'Per-company operations for the TalentHeart suite: surveys, recognitions, reports and programs.'}
            </p>
          </div>
        </div>
        {picked
          ? <CompanyDetail company={picked} onBack={() => setPicked(null)} />
          : (
            <div className="space-y-6">
              {/* Founder UAT — engagement supervision layer (role-aware, full desktop width) */}
              <SuiteOpsPanel suiteCode="talentheart" />
              <CompaniesView onPick={setPicked} />
            </div>
          )}
      </div>
    </div>
  );
}
